import { useMyself } from "@api";
import {
  ROLE_CROSS_MANDATOR_USER_VIEWER,
  ROLE_IMPERSONATE,
  ROLE_MANDATOR_ADMIN,
  ROLE_MANDATOR_CREDENTIALS,
  ROLE_MANDATOR_SCOPED_IMPERSONATE,
  ROLE_ROLE_MANAGEMENT,
  ROLE_USER_MANAGEMENT,
  ROLE_USER_VIEWER,
} from "@constants";

/**
 * Authorization for the User Management area.
 *
 * Each role group below mirrors the Grails AdminController @Secured gates,
 * which are the source of truth for the UI. The backend API remains the real
 * enforcement gate — these checks exist so the UI never offers an action the
 * API would reject (defense in depth + clean UX).
 */

// Mirrors AdminController#showUser @Secured exactly.
// ROLE_MANDATOR_CREDENTIALS is in the v1 API @Secured but is a machine-to-
// machine role that was never part of the Grails UI gate — excluded here.
export const USER_VIEW_ROLES = [
  ROLE_USER_VIEWER,
  ROLE_CROSS_MANDATOR_USER_VIEWER,
  ROLE_USER_MANAGEMENT,
  ROLE_MANDATOR_ADMIN,
];

// PUT v1/users/{username}
const USER_EDIT_ROLES = [
  ROLE_USER_MANAGEMENT,
  ROLE_MANDATOR_ADMIN,
  ROLE_MANDATOR_CREDENTIALS,
];

// PUT/DELETE v1/roles/... — note: ROLE_MANDATOR_ADMIN is NOT in the @Secured
// list for assign/unassign, so it must not unlock these controls on its own.
const ROLE_ASSIGNMENT_ROLES = [ROLE_ROLE_MANAGEMENT, ROLE_MANDATOR_CREDENTIALS];

const hasAny = (roles: string[] | undefined, allowed: string[]): boolean =>
  Boolean(roles?.some((role) => allowed.includes(role)));

// POST /v1/roles (create global role) + DELETE /v1/roles/{id} (delete global role)
// Both gated on ROLE_MANDATOR_ADMIN per Grails RoleApiController @Secured.
export const GLOBAL_ROLE_ADMIN_ROLES = [ROLE_MANDATOR_ADMIN];

// Grant/revoke impersonation permissions on a dev's API token. ROLE_IMPERSONATE
// is cross-mandator; ROLE_MANDATOR_SCOPED_IMPERSONATE is same-mandator only.
export const API_TOKEN_IMPERSONATION_ROLES = [
  ROLE_IMPERSONATE,
  ROLE_MANDATOR_SCOPED_IMPERSONATE,
];

export interface UserManagementPermissions {
  /** May open the section, search and view users. */
  canViewUsers: boolean;
  /** May edit user fields (email, status flags). */
  canEditUser: boolean;
  /** May move a user to a different mandator (mandator-admin only). */
  canChangeMandator: boolean;
  /** May impersonate the user. */
  canImpersonate: boolean;
  /** May assign/remove roles on a user. */
  canManageRoleAssignments: boolean;
  /** May create and delete global roles. */
  canManageGlobalRoles: boolean;
  /** May view a user's API tokens and manage impersonation grants on them. */
  canManageApiTokenImpersonation: boolean;
}

export const useUserManagementPermissions = (): UserManagementPermissions => {
  const { data: myself } = useMyself();
  const roles = myself?.roles;

  return {
    canViewUsers: hasAny(roles, USER_VIEW_ROLES),
    canEditUser: hasAny(roles, USER_EDIT_ROLES),
    canChangeMandator: hasAny(roles, [ROLE_MANDATOR_ADMIN]),
    canImpersonate: hasAny(roles, [ROLE_IMPERSONATE]),
    canManageRoleAssignments: hasAny(roles, ROLE_ASSIGNMENT_ROLES),
    canManageGlobalRoles: hasAny(roles, GLOBAL_ROLE_ADMIN_ROLES),
    canManageApiTokenImpersonation: hasAny(
      roles,
      API_TOKEN_IMPERSONATION_ROLES
    ),
  };
};
